import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getServerInfo } from '../../services/authService';
import { useSession } from '../../context/SessionContext';
import '../../styles/Header.css';

const Header = () => {
  const [serverInfo, setServerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { menuItems, isAdmin, sessionParams, userName, hostName } = useSession();

  // Build hierarchical menu structure
  const buildMenuHierarchy = (items) => {
    const menuMap = {};
    const rootItems = [];
    
    // First pass: create menu map
    items.forEach(item => {
      menuMap[item.menuId] = {
        ...item,
        children: []
      };
    });
    
    // Second pass: build hierarchy
    items.forEach(item => {
      if (item.parentMenuId === 0) {
        // Root level item
        rootItems.push(menuMap[item.menuId]);
      } else if (menuMap[item.parentMenuId]) {
        // Child item - add to parent's children
        menuMap[item.parentMenuId].children.push(menuMap[item.menuId]);
      }
    });
    
    return { menuMap, rootItems };
  };

  const { menuMap, rootItems } = buildMenuHierarchy(menuItems);

  useEffect(() => {
    const fetchServerInfo = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const serverInfoResponse = await getServerInfo();
        setServerInfo(serverInfoResponse);
      } catch (err) {
        console.error('Error fetching server info:', err);
        setServerInfo({ serverId: '01' });
      } finally {
        setLoading(false);
      }
    };
    
    fetchServerInfo();
  }, [isAuthenticated]);

  const handleLogin = () => {
    window.location.href = '/';
  };

  const handleLogout = () => {
    logout();
  };

  // Get username from session context (from Main API) or fallback to user data
  const formattedUsername = loading ? (
    <img src="/ajax-loading.gif" alt="Loading..." style={{ width: '20px', height: '20px' }} />
  ) : (
    userName ? userName.toUpperCase() : 
     (isAuthenticated && user && user.username ? user.username.toUpperCase() : '')
  );

  // Build query string from session parameters
  const buildQueryString = (menuId) => {
    const params = new URLSearchParams();
    if (menuId !== undefined && menuId !== null) {
      params.set('menuId', menuId);
      console.log('🏷️ Setting menuId in query string:', menuId);
    }
    if (sessionParams) {
      Object.entries(sessionParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value);
        }
      });
    }
    const queryString = params.toString();
    console.log('🔍 Built query string:', queryString, 'for menuId:', menuId);
    return queryString;
  };

  // Dynamic navigation items from API response menuItems (excluding Admin and its children)
  const mainNavItems = rootItems
    .filter(item => item.menuName !== 'Admin') // Exclude Admin from main nav
    .map(item => ({
      id: item.menuId,
      name: item.menuName,
      path: `/reports-list?${buildQueryString(item.menuId)}`
    }));

  // Dynamic admin dropdown items from API response
  const adminParent = rootItems.find(item => item.menuName === 'Admin');
  const adminItems = isAdmin && adminParent ? 
    adminParent.children.map(item => ({
      id: item.menuId,
      name: item.menuName,
      path: getNavigationPath(item)
    })) : [];

  // Helper function to determine navigation path based on menuNvgUrl
  function getNavigationPath(item) {
    const navUrl = item.menuNvgUrl || '';
    
    if (navUrl.includes('ManageMenu.aspx')) {
      return `/manage-menu?${buildQueryString()}`;
    } else if (navUrl.includes('ManageReports.aspx')) {
      return `/manage-reports?${buildQueryString()}`;
    } else if (navUrl.includes('ServiceConfig.aspx')) {
      return `/service-config?${buildQueryString()}`;
    } else if (navUrl.includes('ManageRoles.aspx')) {
      return `/manage-roles?${buildQueryString()}`;
    }
    
    // Default fallback
    return `/reports-list?${buildQueryString(item.menuId)}`;
  }

  // Determine if any admin item is active
  const isAdminActive = adminItems.some(item => 
    location.pathname.startsWith(item.path.split('?')[0])
  );

  // Determine which navigation item is active
  const isActive = (path) => {
    const currentPath = location.pathname;
    const currentSearch = location.search;
    
    // Extract menuId from the current URL
    const urlParams = new URLSearchParams(currentSearch);
    const currentMenuId = urlParams.get('menuId');
    
    if (path.includes('?')) {
      // For paths with query parameters, extract the menuId from the path
      const [pathname, search] = path.split('?');
      const pathParams = new URLSearchParams(search);
      const pathMenuId = pathParams.get('menuId');
      
      // If we have a menuId in both current URL and path, compare them
      if (currentMenuId && pathMenuId) {
        return currentMenuId === pathMenuId;
      }
      
      // Original logic for exact path matching
      if (currentPath.startsWith(pathname)) {
        return currentSearch.includes(search);
      }
      return false;
    }
    
    // Special case for the home icon with menuId=0
    if (path === '/reports-list?menuId=0' && (currentPath === '/' || currentPath === '/reports-list') && 
        (!currentSearch || currentSearch === '?menuId=0')) {
      return true;
    }
    return currentPath.startsWith(path.split('?')[0]);
  };

  return (
    <div className="header-container">
      {/* Main navigation bar */}
      <div className="main-nav">
        <div className="left-section">
          <div className="brand">CCA Reports</div>
          <Link to="/reports-list?menuId=1" className="home-icon">
            <i className="fa fa-home"></i>
          </Link>
          
          {/* Main navigation tabs */}
          {isAuthenticated && mainNavItems.map(item => (
            <Link 
              key={item.id}
              to={item.path} 
              className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
            >
              {item.name}
            </Link>
          ))}
          
          {/* Admin dropdown that shows on hover - only if user is admin */}
          {isAuthenticated && isAdmin && adminItems.length > 0 && (
            <div className={`nav-item dropdown ${isAdminActive ? 'active' : ''}`}>
              Admin <span className="dropdown-arrow">▼</span>
              
              <div className="dropdown-menu">
                {adminItems.map(item => (
                  <Link 
                    key={item.id}
                    to={item.path} 
                    className={`dropdown-item ${isActive(item.path) ? 'active' : ''}`}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="right-section">
          <div className="server-info">
            <b>Server: {loading ? (
              <img src="/ajax-loading.gif" alt="Loading..." style={{ width: '20px', height: '20px', marginLeft: '5px' }} />
            ) : (
              hostName 
            )}</b>
          </div>
          <div className="user-info">
            <b>Welcome: {formattedUsername}</b>
          </div>
        </div>
      </div>
      
      {/* Breadcrumb navigation */}
      {isAuthenticated && (
        <div className="breadcrumb">
          <span>
            <Link to="/reports-list?menuId=0" className="breadcrumb-link">Home</Link> &gt; {
              isAdminActive 
                ? (
                  <>
                    <span>Admin</span> &gt; {adminItems.find(item => isActive(item.path))?.name || 'Admin'}
                  </>
                )
                : mainNavItems.find(item => isActive(item.path))?.name || 'Care Management'
            }
          </span>
        </div>
      )}
    </div>
  );
};

export default Header;